// src/components/LazyImage.tsx
import { useEffect, useRef, useState } from "react";
import CachedImage from "./CachedImage";

type Props = React.ImgHTMLAttributes<HTMLImageElement> & {
  placeholder?: string; // optional tiny data URI or blur asset
};

export default function LazyImage({ src, placeholder, onLoad, onError, ...rest }: Props) {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Extract style from rest to avoid overriding our merged style
  const { style: restStyle, ...restWithoutStyle } = rest;

  useEffect(() => {
    // Use containerRef for IntersectionObserver since imgRef might not be attached yet
    const node = containerRef.current;
    if (!node) return;

    if ("IntersectionObserver" in window) {
      // Check if element is already in viewport before setting up observer
      const rect = node.getBoundingClientRect();
      const isInViewport = rect.top < window.innerHeight + 300 && rect.bottom > -300;
      
      if (isInViewport) {
        setVisible(true);
        return;
      }

      const io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting || entry.intersectionRatio > 0) {
              setVisible(true);
              io.disconnect();
              break;
            }
          }
        },
        {
          // Preload a bit before entering viewport so images are ready when scrolled to
          root: null,
          rootMargin: "300px 0px",
          threshold: 0.01,
        }
      );
      io.observe(node);
      return () => io.disconnect();
    }

    // Fallback (very old WebViews): just load the image
    setVisible(true);
  }, [src]);

  // Use CachedImage for the actual image loading (handles bundled assets, cache, and download)
  // Combine internal onLoad handler with parent's onLoad if provided
  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setLoaded(true);
    if (onLoad) {
      onLoad(e);
    }
  };

  // Always render container for IntersectionObserver, but conditionally render content
  // Use a wrapper that participates in layout so IntersectionObserver can track it
  return (
    <span ref={containerRef} style={{ display: 'inline-block', width: '100%', height: '100%' }}>
      {!visible && placeholder ? (
        <img
          src={placeholder}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : visible ? (
        <CachedImage
          ref={imgRef}
          src={src ?? null}
          onLoad={handleLoad}
          onError={onError}
          style={{
            transition: "filter 200ms ease",
            filter: loaded ? "blur(0px)" : "blur(8px)",
            ...restStyle,
          }}
          {...restWithoutStyle}
        />
      ) : (
        // Render invisible placeholder so IntersectionObserver can track dimensions
        <div style={{ width: '100%', height: '100%', minHeight: '1px' }} aria-hidden="true" />
      )}
    </span>
  );
}
